/** Task categories: built-ins (renamable/hideable) + user-defined, stored on device. */
export interface Category {
  id: string;
  name: string;
}

const KEY = "nelurio.categories.v1";
const OVERRIDES_KEY = "nelurio.categories.overrides.v1";

export const BUILTIN_KINDS = ["study", "assignment", "exam", "project"] as const;

export const BUILTIN_KEY: Record<string, string> = {
  study: "dashboard.study",
  assignment: "dashboard.assignment",
  exam: "dashboard.exam",
  project: "task.project"
};

/** Server-generated kinds (class reports etc.) — shown in edit mode only. */
export const SERVER_KINDS = ["class", "new_lesson", "review"];

export const BUILTIN_LABELS: Record<string, string> = {
  class: "dashboard.class",
  new_lesson: "dashboard.newLesson",
  review: "reviews.title"
};

export interface CategoryOverrides {
  /** renamed built-in kind -> new label */
  renamed: Record<string, string>;
  /** hidden built-in kinds */
  hidden: string[];
}

export function readOverrides(): CategoryOverrides {
  try {
    const value = JSON.parse(window.localStorage.getItem(OVERRIDES_KEY) ?? "{}");
    return {
      renamed: value?.renamed && typeof value.renamed === "object" ? value.renamed : {},
      hidden: Array.isArray(value?.hidden) ? value.hidden : []
    };
  } catch {
    return { renamed: {}, hidden: [] };
  }
}

function writeOverrides(overrides: CategoryOverrides) {
  try { window.localStorage.setItem(OVERRIDES_KEY, JSON.stringify(overrides)); } catch { /* ignore */ }
}

export function renameBuiltin(kind: string, name: string): CategoryOverrides {
  const overrides = readOverrides();
  if (name.trim()) overrides.renamed[kind] = name.trim();
  else delete overrides.renamed[kind];
  writeOverrides(overrides);
  return overrides;
}

export function toggleBuiltinHidden(kind: string): CategoryOverrides {
  const overrides = readOverrides();
  overrides.hidden = overrides.hidden.includes(kind)
    ? overrides.hidden.filter((item) => item !== kind)
    : [...overrides.hidden, kind];
  writeOverrides(overrides);
  return overrides;
}

export function visibleBuiltins(): string[] {
  const { hidden } = readOverrides();
  return BUILTIN_KINDS.filter((kind) => !hidden.includes(kind));
}

export function readCategories(): Category[] {
  try {
    const value = JSON.parse(window.localStorage.getItem(KEY) ?? "[]");
    return Array.isArray(value) ? value.filter((item) => item && item.id && item.name) : [];
  } catch {
    return [];
  }
}

export function writeCategories(list: Category[]) {
  try { window.localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

export function addCategory(name: string): Category[] {
  const list = readCategories();
  const next = [...list, { id: crypto.randomUUID(), name: name.trim() }];
  writeCategories(next);
  return next;
}

export function renameCategory(id: string, name: string): Category[] {
  const next = readCategories().map((item) => (item.id === id ? { ...item, name: name.trim() } : item));
  writeCategories(next);
  return next;
}

export function deleteCategory(id: string): Category[] {
  const next = readCategories().filter((item) => item.id !== id);
  writeCategories(next);
  return next;
}

/** Resolve a task's kind to a display label — overrides first, then i18n label,
 *  then the raw kind (user categories carry their name as the kind). */
export function categoryLabel(kind: string, t: (key: string) => string): string {
  const { renamed } = readOverrides();
  if (renamed[kind]) return renamed[kind];
  if (BUILTIN_KEY[kind] || BUILTIN_LABELS[kind]) return t(BUILTIN_KEY[kind] ?? BUILTIN_LABELS[kind]);
  return kind;
}
